/**
 * MetadataEditorPanel — OrgPilot local copy.
 * Fixed-width panel using @ncoder/code-editor (tabs + format, no AI/Deploy/Sync).
 * Resize handle lives in DevAgent.tsx (sibling column) so Monaco scrollbars stay clear.
 */

import { TabbedCodeEditor } from "@ncoder/code-editor";
import { AnalyzingShimmerBar } from "./metadataEditor/AnalyzingShimmerBar";
import { SaveErrorsBanner } from "./metadataEditor/SaveErrorsBanner";
import { AutoFixStatusBars } from "./metadataEditor/AutoFixStatusBars";
import { MetadataEditorPanelHeader } from "./metadataEditor/MetadataEditorPanelHeader";
import { AcceptedCodeDiffSection } from "./metadataEditor/AcceptedCodeDiffSection";
import type { MetadataEditorPanelProps } from "./metadataEditor/types";
import { useMetadataEditorPanel } from "./metadataEditor/useMetadataEditorPanel";

export type { OpenFile, MetadataEditorPanelProps } from "./metadataEditor/types";

export function MetadataEditorPanel(props: MetadataEditorPanelProps) {
  const {
    panelWidth,
    activeFileId,
    extraActions,
    isAnalyzing,
    onRunTest,
    acceptedCode,
    onLoadIntoChat,
    onAcceptedCodeChange,
    isBusy,
    onReorderTabs,
  } = props;

  const vm = useMetadataEditorPanel(props);

  if (vm.shouldRenderNull) return null;

  const loadIntoChatHandler =
    onLoadIntoChat &&
    vm.activeFile &&
    vm.activeFile.name !== "rules.cls"
      ? () => {
          const f = vm.activeFile;
          if (!f) return;
          onLoadIntoChat({
            id: f.id,
            name: f.name,
            metadataType: f.metadataType,
            content:
              vm.contentByFileId[f.id] ??
              vm.metadataFileData?.content ??
              vm.originalByFileId[f.id] ??
              "",
          });
        }
      : undefined;

  return (
    <div
      className="flex h-full min-h-0 shrink-0 flex-col overflow-hidden"
      style={{ width: panelWidth }}
    >
      <div
        className="flex min-h-0 flex-1 flex-col overflow-hidden rounded-lg border border-border bg-card"
        data-resizable-editor
      >
        <div className="flex h-full min-h-0 flex-col bg-background">
          <MetadataEditorPanelHeader
            isLoadingMetadataFile={vm.isLoadingMetadataFile}
            activeFile={vm.activeFile}
            activeBadge={vm.activeBadge}
            extraActions={extraActions}
            onRunTest={onRunTest}
            activeFileId={activeFileId}
            editorContent={vm.contentByFileId[activeFileId ?? ""] ?? ""}
            isBusy={isBusy}
            onClose={vm.handleClose}
          />

          <AnalyzingShimmerBar visible={vm.isSaving || !!isAnalyzing} />

          {vm.saveErrors && (
            <SaveErrorsBanner
              saveErrors={vm.saveErrors}
              isSaving={vm.isSaving}
              onDismiss={() => vm.setSaveErrors(null)}
            />
          )}

          <AutoFixStatusBars
            autoFixState={vm.autoFixState}
            errorExpanded={vm.errorExpanded}
            setErrorExpanded={vm.setErrorExpanded}
            onCancelAutoFix={vm.cancelAutoFix}
            onResetAutoFix={vm.resetAutoFix}
          />

          <div className="flex min-h-0 flex-1 flex-col overflow-hidden">
            {vm.metadataFileError &&
              activeFileId &&
              !vm.isVirtualFile &&
              vm.contentByFileId[activeFileId] === undefined && (
                <div className="flex flex-1 items-center justify-center p-4 text-center">
                  <div>
                    <p className="text-sm text-destructive">Failed to load file</p>
                    <p className="mt-1 text-xs text-muted-foreground">
                      {vm.metadataFileError.message}
                    </p>
                  </div>
                </div>
              )}

            {vm.showDiff && activeFileId && acceptedCode && (
              <AcceptedCodeDiffSection
                activeFileId={activeFileId}
                activeFile={vm.activeFile}
                acceptedCode={acceptedCode}
                originalContentForDiff={vm.originalContentForDiff}
                editorTheme={vm.editorTheme}
                diffEditorRef={vm.diffEditorRef}
                diffOverlayOpen={vm.diffOverlayOpen}
                setDiffOverlayOpen={vm.setDiffOverlayOpen}
                diffHunkTotal={vm.diffHunkTotal}
                diffHunkResolved={vm.diffHunkResolved}
                setDiffHunkTotal={vm.setDiffHunkTotal}
                setDiffHunkResolved={vm.setDiffHunkResolved}
                setContentByFileId={vm.setContentByFileId}
                setOriginalByFileId={vm.setOriginalByFileId}
                setDirtyByFileId={vm.setDirtyByFileId}
                onAcceptedCodeChange={onAcceptedCodeChange}
                handleSave={vm.handleSave}
              />
            )}

            {vm.showTabbedEditor && (
              <>
                {vm.isLoadingMetadataFile &&
                vm.packageTabs.every(
                  (t) => t.value === "" && t.id === activeFileId,
                ) ? (
                  <div className="flex flex-1 items-center justify-center">
                    <div className="flex flex-col items-center gap-2">
                      <div className="h-6 w-6 animate-spin rounded-full border-2 border-primary border-t-transparent" />
                      <p className="text-xs text-muted-foreground">Loading file...</p>
                    </div>
                  </div>
                ) : (
                  <TabbedCodeEditor
                    tabs={vm.packageTabs}
                    activeTabId={activeFileId}
                    onTabChange={vm.handleTabChange}
                    onTabClose={vm.handleTabClose}
                    onTabReorder={onReorderTabs}
                    onContentChange={vm.handleContentChange}
                    theme={vm.editorTheme}
                    formatApi={vm.formatApi}
                    lintApi={vm.lintApi}
                    onSave={vm.showDiff ? undefined : vm.handleSave}
                    onAbandon={vm.activeBranch ? vm.handleAbandon : undefined}
                    onToggleViewMain={
                      vm.activeBranch ? vm.handleToggleViewMain : undefined
                    }
                    branchName={vm.activeBranch?.name}
                    viewingMain={vm.viewingMain}
                    isAbandoning={vm.isAbandoning}
                    onLoadIntoChat={loadIntoChatHandler}
                    externalMarkers={
                      vm.isVirtualFile || vm.metadataFileError
                        ? []
                        : [...(vm.analysisMarkers || []), ...vm.coverageMarkers]
                    }
                    coverageLines={
                      vm.covData &&
                      vm.activeFile &&
                      vm.covData.className ===
                        vm.activeFile.name?.replace(/\.(cls|trigger)$/, "")
                        ? vm.covData
                        : undefined
                    }
                    isLoadingContent={
                      vm.isLoadingMetadataFile &&
                      vm.contentByFileId[activeFileId ?? ""] === undefined
                    }
                    isDirty={!!vm.isDirty}
                    isSaving={vm.isSaving || vm.isBranchSaving}
                    editorOptions={vm.editorOptions}
                    className="min-h-0 flex-1"
                  />
                )}
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
